import { ChainUpgrade, ChainStepDef, EnemyType } from './types';
import {
  UNIT_BUY_COSTS, ENEMY_SCALING, PRESTIGE_BALANCE, UNIT_STATS, WALL,
  ENEMY_BASE as _ENEMY_BASE, ENEMY_TYPES as _ENEMY_TYPES, BOSS_DEFINITIONS as _BOSS_DEFINITIONS,
  WAVE as _WAVE, ELDER_COUNCIL as _ELDER_COUNCIL
} from './balance';
import { buildDamageChain, buildRepairChain, buildSwarmChain } from './chainGenerator';

// ---- DISPLAY ----
export const GAME_WIDTH = 1024;
export const GAME_HEIGHT = 600;

// ---- PERSPECTIVE ----
// Angled top-down view: horizon at top, ground fills most of screen
export const HORIZON_Y = 80;
export const GROUND_Y = HORIZON_Y; // ground starts at horizon

// ---- LAYOUT ----
export const WALL_X = 240; // Shifted left to give more field space
export const WALL_WIDTH = 30; // Widened so units don't overhang
// Wall spans the full playable ground area
export const WALL_Y_START = HORIZON_Y + 10;
export const WALL_Y_END = GAME_HEIGHT; // Reaches all the way down
export const WALL_BASE_HP = WALL.baseHP;

// ---- BASE AREA ----
// Grid slots for units behind the wall (left side)
// 2 columns x 4 rows of placement slots
export const BASE_SLOT_COLS = 2;
export const BASE_SLOT_ROWS = 4;
export const BASE_SLOT_START_X = 70;
export const BASE_SLOT_END_X = 150;
export const BASE_SLOT_START_Y = HORIZON_Y + 40;
export const BASE_SLOT_END_Y = 470;
export const BASE_SLOT_RADIUS = 22;

// Wall slots (on the wall itself) - 4 positions along the wall
export const WALL_SLOT_COUNT = 5;

// Dedicated builder slot - positioned right behind the wall, close enough to hammer.
export const BUILDER_SLOT = {
  x: WALL_X - 25,
  y: (WALL_Y_START + WALL_Y_END) / 2, // Center of wall
};

// ---- INCREMENTAL SCALING CONSTANTS ----
// Enemy stats come from ENEMY_SCALING in balance.ts — edit there to tune scaling.
export const SCALING = {
  unitCostScale: 1.15,                          // Cost multiplier per unit owned
  chainCostScale: 2.8,                          // Cost multiplier per chain step
  enemyHpGrowth: ENEMY_SCALING.hpGrowthPerWave,
  enemyDmgGrowth: ENEMY_SCALING.dmgGrowthPerWave,
  enemyGoldGrowth: ENEMY_SCALING.goldGrowthPerWave,
  enemyBaseHp: ENEMY_SCALING.baseHp,
  enemyBaseDmg: ENEMY_SCALING.baseDmg,
  enemyBaseGold: ENEMY_SCALING.baseGold,
  wallHpGrowth: WALL.hpGrowthPerUpgrade,         // Wall HP multiplier per level (+25% per upgrade)
  skillUpgradeCostScale: 2.0,                   // Skill upgrade cost doubles per level
};

// ---- BUY COSTS (exponential: base × 1.15^unitsOwned) ----
// All values defined in src/balance.ts UNIT_BUY_COSTS — edit there.
export const BUY_BASE_COSTS: Record<string, number> = UNIT_BUY_COSTS;

// Legacy BUY_COSTS — kept for compatibility, wraps BUY_BASE_COSTS
export const BUY_COSTS: Record<string, { base: number; perUnit: number }> = Object.fromEntries(
  Object.entries(BUY_BASE_COSTS).map(([key, base]) => [key, { base, perUnit: 0 }])
);

// ---- UPGRADE BASE COSTS (exponential: base * 1.5^level) — repeatable upgrades only ----
// click_damage, builder_efficiency, Gold%, Range%, All Damage% removed — handled by chain upgrades and Prestige
export const UPGRADE_BASE_COSTS: Record<string, number> = {
  wall_upgrade: 60,
  // Skill upgrade base costs (use buyCost × 2.0^level)
  poison_arrows: 1500,
  rock_rain: 8000,
  gold_rush: 25000,
  lightning_strike: 75000,
};

// ---- CHAIN UPGRADES (sequential one-time upgrades per unit) ----
// Costs and descriptions are AUTO-GENERATED from src/balance.ts CHAIN_CONFIG.
// To scale a whole unit's damage: change CHAIN_CONFIG[unit].powerMult in balance.ts.
// To shift a whole unit's costs: change CHAIN_CONFIG[unit].firstStepCost in balance.ts.
// To override a specific step's cost: add costOverride: N to that step's def below.
export const UPGRADE_CHAINS: Record<string, ChainUpgrade[]> = {
  finger: [
    { id: 'finger_1', name: 'Sharp Nails', description: '+3 click damage', cost: 40, effect: { fingerDamageFlat: 3 } },
    { id: 'finger_2', name: 'Gold Touch', description: '+25% click damage, +1g per click', cost: 120, effect: { fingerDamageMult: 0.25, fingerClickGold: 1 } },
    { id: 'finger_3', name: 'Heavy Finger', description: '+8 click damage', cost: 439, effect: { fingerDamageFlat: 8 } },
    { id: 'finger_4', name: 'Shockwave', description: '+40% click damage, 50px AoE', cost: 1230, effect: { fingerDamageMult: 0.40, fingerAoeRadius: 50 } },
    { id: 'finger_5', name: 'Iron Knuckles', description: '+16 click damage', cost: 3444, effect: { fingerDamageFlat: 16 } },
    { id: 'finger_6', name: 'Midas Touch', description: '+55% click damage, +5g per click', cost: 9643, effect: { fingerDamageMult: 0.55, fingerClickGold: 5 } },
    { id: 'finger_7', name: 'Thunderfist', description: '+25 click damage', cost: 27000, effect: { fingerDamageFlat: 25 } },
    { id: 'finger_8', name: 'Earthquake', description: '+65% click damage, 90px AoE', cost: 75600, effect: { fingerDamageMult: 0.65, fingerAoeRadius: 90 } },
    { id: 'finger_9', name: 'Titan Finger', description: '+150 click damage', cost: 211680, effect: { fingerDamageFlat: 50 } },
    { id: 'finger_10', name: 'Critical Mass', description: '+75% click damage', cost: 592704, effect: { fingerDamageMult: 0.75 } },
    { id: 'finger_11', name: 'Meteor Strike', description: '+100 click damage', cost: 1659571, effect: { fingerDamageFlat: 100 } },
    { id: 'finger_12', name: 'Golden Fist', description: '+80% click damage, +50g per click', cost: 4646800, effect: { fingerDamageMult: 0.80, fingerClickGold: 50 } },
    { id: 'finger_13', name: 'Godhand', description: '+200 click damage', cost: 13011040, effect: { fingerDamageFlat: 200 } },
    { id: 'finger_14', name: 'Cataclysm', description: '+85% click damage, 150px AoE', cost: 36430912, effect: { fingerDamageMult: 0.85, fingerAoeRadius: 150 } },
    { id: 'finger_15', name: 'Auto Finger', description: '+400 click damage, auto-click 2/sec', cost: 102006554, effect: { fingerDamageFlat: 400, fingerAutoClick: 500 } },
    { id: 'finger_16', name: 'Quake Fist', description: '+90% click damage, 200px AoE', cost: 285618351, effect: { fingerDamageMult: 0.90, fingerAoeRadius: 200 } },
    { id: 'finger_17', name: 'Titan Knuckles', description: '+800 click damage', cost: 799731383, effect: { fingerDamageFlat: 800 } },
    { id: 'finger_18', name: 'Cosmic Touch', description: '+95% click damage, +200g per click', cost: 2239247872, effect: { fingerDamageMult: 0.95, fingerClickGold: 200 } },
    { id: 'finger_19', name: 'World Breaker', description: '+1600 click damage', cost: 6269894041, effect: { fingerDamageFlat: 1600 } },
    { id: 'finger_20', name: 'Apocalypse Fist', description: '+100% click damage, 280px AoE', cost: 17555703315, effect: { fingerDamageMult: 1.00, fingerAoeRadius: 280 } },
    { id: 'finger_21', name: 'Armageddon Strike', description: '+3200 click damage', cost: 49155969282, effect: { fingerDamageFlat: 3200 } },
    { id: 'finger_22', name: 'Extinction Touch', description: '+100% click damage, +1000g per click', cost: 137636713990, effect: { fingerDamageMult: 1.00, fingerClickGold: 1000 } },
    { id: 'finger_23', name: 'Universe Fist', description: '+6400 click damage, 350px AoE', cost: 385382799172, effect: { fingerDamageFlat: 6400, fingerAoeRadius: 350 } },
    { id: 'finger_24', name: 'Big Bang', description: '+100% click damage', cost: 1079071837682, effect: { fingerDamageMult: 1.00 } },
    { id: 'finger_25', name: 'The Final Finger', description: '+12800 click damage, 450px AoE, auto 5/sec', cost: 3021401145510, effect: { fingerDamageFlat: 12800, fingerAoeRadius: 450, fingerAutoClick: 200 } },
  ],
  rock_thrower: buildDamageChain('rock_thrower', [
    { id: 'rock_sharp', name: 'Sharp Stones', damageBonus: 0.30, effect: {} },
    { id: 'rock_extra1', name: 'Extra Stone', damageBonus: 0.35, suffix: '+1 projectile', effect: { extraProjectiles: 1 } },
    { id: 'rock_heavy', name: 'Heavy Rocks', damageBonus: 0.40, effect: {} },
    { id: 'rock_extra2', name: 'Extra Stone II', damageBonus: 0.40, suffix: '+1 projectile', effect: { extraProjectiles: 1 } },
    { id: 'rock_boulder', name: 'Boulder Toss', damageBonus: 0.45, effect: {} },
    { id: 'rock_rapid', name: 'Rapid Throw', damageBonus: 0.50, suffix: '-30% attack interval', effect: { attackIntervalMult: 0.7 } },
    { id: 'rock_extra3', name: 'Extra Stone III', damageBonus: 0.50, suffix: '+1 projectile', effect: { extraProjectiles: 1 } },
    { id: 'rock_stun', name: 'Concussive Impact', damageBonus: 0.50, suffix: 'stun 1s', effect: { stunDuration: 1000 } },
    { id: 'rock_8', name: 'Crushing Force', damageBonus: 0.55, effect: {} },
    { id: 'rock_9', name: 'Volcanic Barrage', damageBonus: 0.55, effect: {} },
    { id: 'rock_10', name: 'Meteor Shower', damageBonus: 0.65, effect: {} },
    { id: 'rock_11', name: 'Earthquake Stones', damageBonus: 0.45, suffix: '+1.5s stun', effect: { stunDuration: 1500 } },
    { id: 'rock_12', name: 'Avalanche', damageBonus: 0.50, effect: {} },
    { id: 'rock_13', name: "Mountain's Fury", damageBonus: 0.80, suffix: '+1 projectile', effect: { extraProjectiles: 1 } },
    { id: 'rock_14', name: 'Cosmic Bombardment', damageBonus: 0.55, suffix: '+2s stun', effect: { stunDuration: 2000 } },
    { id: 'rock_15', name: 'Stellar Rain', damageBonus: 0.35, suffix: '-20% interval', effect: { attackIntervalMult: 0.80 } },
    { id: 'rock_16', name: 'Nebula Shower', damageBonus: 0.60, suffix: '+1 projectile', effect: { extraProjectiles: 1 } },
    { id: 'rock_17', name: 'Galaxy Impact', damageBonus: 0.60, suffix: '+3s stun', effect: { stunDuration: 3000 } },
    { id: 'rock_18', name: 'Quasar Barrage', damageBonus: 0.85, effect: {} },
    { id: 'rock_19', name: 'Supernova Stones', damageBonus: 0.90, suffix: '+1 projectile', effect: { extraProjectiles: 1 } },
    { id: 'rock_20', name: 'Cosmic Deluge', damageBonus: 0.40, suffix: '-20% interval', effect: { attackIntervalMult: 0.80 } },
    { id: 'rock_21', name: 'Gravity Crush', damageBonus: 0.65, suffix: '+4s stun', effect: { stunDuration: 4000 } },
    { id: 'rock_22', name: 'Singularity Shot', damageBonus: 0.70, suffix: '+1 projectile', effect: { extraProjectiles: 1 } },
    { id: 'rock_23', name: 'Universe Shatter', damageBonus: 0.95, suffix: '+5s stun', effect: { stunDuration: 5000 } },
    { id: 'rock_24', name: 'Big Bang Stone', damageBonus: 1.00, effect: {} },
    { id: 'rock_25', name: 'Omega Bombardment', damageBonus: 1.00, suffix: '+1 projectiles, -20% interval', effect: { extraProjectiles: 1, attackIntervalMult: 0.80 } },
  ] as ChainStepDef[]),
  archer: buildDamageChain('archer', [
    { id: 'arch_aim', name: 'Steady Aim', damageBonus: 0.15, suffix: '+10% accuracy', effect: { accuracyPenaltySet: 0.40 } },
    { id: 'arch_poison', name: 'Poison Tips', damageBonus: 0.15, suffix: 'poison 15% dmg/tick 3s', effect: { poisonEnabled: true, poisonDurationBonus: 3000 } },
    { id: 'arch_rapid', name: 'Rapid Shot', damageBonus: 0.30, suffix: '-25% attack interval', effect: { attackIntervalMult: 0.75 } },
    { id: 'arch_deadly', name: 'Deadly Poison', damageBonus: 0.45, suffix: '+10% accuracy, +10% poison, +1s', effect: { accuracyPenaltySet: 0.30, poisonPercentBonus: 0.10, poisonDurationBonus: 1000 } },
    { id: 'arch_eagle', name: 'Eagle Eye', damageBonus: 0.55, suffix: '+10% accuracy', effect: { accuracyPenaltySet: 0.20 } },
    { id: 'arch_double', name: 'Double Shot', damageBonus: 0.30, suffix: 'fire 2 arrows', effect: { doubleShot: true } },
    { id: 'arch_venom', name: 'Venom Master', damageBonus: 0.35, suffix: '+15% poison, +2s', effect: { poisonPercentBonus: 0.15, poisonDurationBonus: 2000 } },
    { id: 'arch_7', name: 'Toxin Mastery', damageBonus: 0.40, suffix: '+10% accuracy', effect: { accuracyPenaltySet: 0.10 } },
    { id: 'arch_8', name: 'Serpent Arrows', damageBonus: 0.40, suffix: '+20% poison', effect: { poisonPercentBonus: 0.20 } },
    { id: 'arch_9', name: 'Death Shot', damageBonus: 0.40, effect: {} },
    { id: 'arch_10', name: 'Plague Bearer', damageBonus: 0.50, suffix: '+25% poison, +3s', effect: { poisonPercentBonus: 0.25, poisonDurationBonus: 3000 } },
    { id: 'arch_11', name: 'Marksman', damageBonus: 0.50, suffix: '+5% accuracy', effect: { accuracyPenaltySet: 0.05 } },
    { id: 'arch_12', name: 'Venom Typhoon', damageBonus: 0.50, suffix: '+30% poison', effect: { poisonPercentBonus: 0.30 } },
    { id: 'arch_13', name: 'Arrow of Extinction', damageBonus: 0.55, effect: {} },
    { id: 'arch_14', name: 'Deadeye God', damageBonus: 0.55, suffix: '+30% poison', effect: { poisonPercentBonus: 0.30 } },
    { id: 'arch_15', name: 'Plague Shot', damageBonus: 0.60, effect: {} },
    { id: 'arch_16', name: 'Venom Typhoon II', damageBonus: 0.60, suffix: '+5s poison', effect: { poisonDurationBonus: 5000 } },
    { id: 'arch_17', name: 'Extinction Rain', damageBonus: 0.65, suffix: 'double shot', effect: { doubleShot: true } },
    { id: 'arch_18', name: 'Toxic Storm', damageBonus: 0.65, suffix: '+40% poison', effect: { poisonPercentBonus: 0.40 } },
    { id: 'arch_19', name: 'Cosmic Arrow', damageBonus: 0.70, effect: {} },
    { id: 'arch_20', name: 'Infinity Quiver', damageBonus: 0.70, suffix: '+5 pierce', effect: { pierceCountBonus: 5 } },
    { id: 'arch_21', name: 'Apocalypse Rain', damageBonus: 0.75, suffix: '+6s poison', effect: { poisonDurationBonus: 6000 } },
    { id: 'arch_22', name: 'Void Arrow', damageBonus: 0.75, effect: {} },
    { id: 'arch_23', name: 'Death Salvo', damageBonus: 0.75, suffix: '+50% poison', effect: { poisonPercentBonus: 0.50 } },
    { id: 'arch_24', name: 'Galactic Volley', damageBonus: 0.80, suffix: '+8 pierce', effect: { pierceCountBonus: 8 } },
    { id: 'arch_25', name: 'Omega Archer', damageBonus: 0.80, suffix: '+8s poison, double shot', effect: { poisonDurationBonus: 8000, doubleShot: true } },
  ] as ChainStepDef[]),
  club_swinger: buildDamageChain('club_swinger', [
    { id: 'club_wider', name: 'Wider Swing', damageBonus: 0.15, suffix: '+20px AoE', effect: { aoeRadiusBonus: 20 } },
    { id: 'club_heavy', name: 'Heavy Club', damageBonus: 0.30, effect: {} },
    { id: 'club_target1', name: 'Extra Target', damageBonus: 0.22, suffix: '+1 max target', effect: { maxTargetsBonus: 1 } },
    { id: 'club_iron', name: 'Iron Club', damageBonus: 1.00, effect: {} },
    { id: 'club_sweep', name: 'Sweeping Blow', damageBonus: 0.76, suffix: '+30px AoE', effect: { aoeRadiusBonus: 30 } },
    { id: 'club_target2', name: 'Extra Target II', damageBonus: 0.85, suffix: '+2 max targets', effect: { maxTargetsBonus: 2 } },
    { id: 'club_berserk', name: 'Berserker', damageBonus: 0.90, suffix: '-40% attack interval', effect: { attackIntervalMult: 0.6 } },
    { id: 'club_disarm', name: 'Crushing Blow', damageBonus: 0.90, suffix: 'disarm 1s', effect: { disarmDuration: 1000 } },
    { id: 'club_8', name: 'Titan Club', damageBonus: 0.55, effect: {} },
    { id: 'club_9', name: 'Shockwave Slam', damageBonus: 0.60, suffix: '+40px AoE', effect: { aoeRadiusBonus: 40 } },
    { id: 'club_10', name: 'Devastator', damageBonus: 0.65, effect: {} },
    { id: 'club_11', name: 'Annihilator', damageBonus: 0.70, suffix: '+3 max targets', effect: { maxTargetsBonus: 3 } },
    { id: 'club_12', name: 'Apocalypse Swing', damageBonus: 0.70, effect: {} },
    { id: 'club_13', name: 'Ragnarok', damageBonus: 0.75, suffix: '+2s disarm', effect: { disarmDuration: 2000 } },
    { id: 'club_14', name: 'World Breaker', damageBonus: 0.75, effect: {} },
    { id: 'club_15', name: 'Titan Swing', damageBonus: 0.80, suffix: '+50px AoE', effect: { aoeRadiusBonus: 50 } },
    { id: 'club_16', name: 'Colossus Blow', damageBonus: 0.80, suffix: '+3 max targets', effect: { maxTargetsBonus: 3 } },
    { id: 'club_17', name: 'Seismic Slam', damageBonus: 0.85, effect: {} },
    { id: 'club_18', name: 'Tectonic Crash', damageBonus: 0.85, suffix: '+3s disarm', effect: { disarmDuration: 3000 } },
    { id: 'club_19', name: 'Earthquake Master', damageBonus: 0.90, suffix: '+60px AoE', effect: { aoeRadiusBonus: 60 } },
    { id: 'club_20', name: 'Planet Slammer', damageBonus: 0.90, suffix: '+4 max targets', effect: { maxTargetsBonus: 4 } },
    { id: 'club_21', name: 'Gravity Hammer', damageBonus: 0.95, suffix: '+4s disarm', effect: { disarmDuration: 4000 } },
    { id: 'club_22', name: 'Cosmic Clubber', damageBonus: 0.95, suffix: '+80px AoE', effect: { aoeRadiusBonus: 80 } },
    { id: 'club_23', name: 'Universe Smash', damageBonus: 0.95, suffix: '+5 max targets', effect: { maxTargetsBonus: 5 } },
    { id: 'club_24', name: 'Big Bang Bash', damageBonus: 1.00, suffix: '+5s disarm', effect: { disarmDuration: 5000 } },
    { id: 'club_25', name: 'Omega Club', damageBonus: 1.00, suffix: '+100px AoE, +6 targets', effect: { aoeRadiusBonus: 100, maxTargetsBonus: 6 } },
  ] as ChainStepDef[]),
  sling_thrower: buildDamageChain('sling_thrower', [
    { id: 'sling_heavy', name: 'Heavier Stones', damageBonus: 0.15, effect: {} },
    { id: 'sling_mud', name: 'Sticky Mud', damageBonus: 0.15, suffix: '+10% slow', effect: { slowEffectBonus: 0.1 } },
    { id: 'sling_long', name: 'Longer Slow', damageBonus: 0.28, suffix: '+1s slow duration', effect: { slowDurationBonus: 1000 } },
    { id: 'sling_double', name: 'Double Sling', damageBonus: 0.56, suffix: '+1 projectile', effect: { extraProjectiles: 1 } },
    { id: 'sling_crush', name: 'Crushing Impact', damageBonus: 0.60, effect: {} },
    { id: 'sling_freeze', name: 'Deep Freeze', damageBonus: 0.65, suffix: '+10% slow, +1s', effect: { slowEffectBonus: 0.1, slowDurationBonus: 1000 } },
    { id: 'sling_triple', name: 'Triple Sling', damageBonus: 0.75, suffix: '+1 projectile', effect: { extraProjectiles: 1 } },
    { id: 'sling_7', name: 'Boulder Barrage', damageBonus: 0.55, effect: {} },
    { id: 'sling_8', name: 'Glacial Sling', damageBonus: 0.60, suffix: '+10% slow', effect: { slowEffectBonus: 0.1 } },
    { id: 'sling_9', name: 'Arctic Bombardment', damageBonus: 0.65, effect: {} },
    { id: 'sling_10', name: 'Frozen Cataclysm', damageBonus: 0.65, suffix: '+2s slow', effect: { slowDurationBonus: 2000 } },
    { id: 'sling_11', name: 'Eternal Winter', damageBonus: 0.70, effect: {} },
    { id: 'sling_12', name: 'Ice Age', damageBonus: 0.70, suffix: '+1 projectiles', effect: { extraProjectiles: 1 } },
    { id: 'sling_13', name: 'Absolute Zero', damageBonus: 0.75, effect: {} },
    { id: 'sling_14', name: 'Polar Barrage', damageBonus: 0.75, suffix: '+3s slow', effect: { slowDurationBonus: 3000 } },
    { id: 'sling_15', name: 'Cryo Storm', damageBonus: 0.80, suffix: '+1 projectiles', effect: { extraProjectiles: 1 } },
    { id: 'sling_16', name: 'Frozen Void', damageBonus: 0.80, effect: {} },
    { id: 'sling_17', name: 'Arctic Annihilation', damageBonus: 0.85, suffix: '+10% slow', effect: { slowEffectBonus: 0.10 } },
    { id: 'sling_18', name: 'Blizzard God', damageBonus: 0.85, suffix: '+4s slow', effect: { slowDurationBonus: 4000 } },
    { id: 'sling_19', name: 'Deep Freeze God', damageBonus: 0.90, suffix: '+1 projectiles', effect: { extraProjectiles: 1 } },
    { id: 'sling_20', name: 'Permafrost', damageBonus: 0.90, effect: {} },
    { id: 'sling_21', name: 'Glacial God', damageBonus: 0.95, suffix: '+10% slow', effect: { slowEffectBonus: 0.10 } },
    { id: 'sling_22', name: 'Ice Age II', damageBonus: 0.95, suffix: '+5s slow', effect: { slowDurationBonus: 5000 } },
    { id: 'sling_23', name: 'Absolute Cold', damageBonus: 0.95, suffix: '+1 projectiles', effect: { extraProjectiles: 1 } },
    { id: 'sling_24', name: 'Omega Freeze', damageBonus: 1.00, effect: {} },
    { id: 'sling_25', name: 'Universal Cryo', damageBonus: 1.00, suffix: '+10% slow, +2s slow', effect: { slowEffectBonus: 0.10, slowDurationBonus: 2000 } },
  ] as ChainStepDef[]),
  spear_thrower: buildDamageChain('spear_thrower', [
    { id: 'spear_sharp', name: 'Sharpened Tips', damageBonus: 0.15, effect: {} },
    { id: 'spear_pierce1', name: 'Extra Pierce', damageBonus: 0.15, suffix: '+1 pierce', effect: { pierceCountBonus: 1 } },
    { id: 'spear_long', name: 'Long Spear', damageBonus: 0.35, effect: {} },
    { id: 'spear_pierce2', name: 'Extra Pierce II', damageBonus: 0.30, suffix: '+1 pierce', effect: { pierceCountBonus: 1 } },
    { id: 'spear_javelin', name: 'Javelin Mastery', damageBonus: 0.35, suffix: '-30% attack interval', effect: { attackIntervalMult: 0.7 } },
    { id: 'spear_serrate', name: 'Serrated Edge', damageBonus: 0.40, effect: {} },
    { id: 'spear_pierce3', name: 'Extra Pierce III', damageBonus: 0.40, suffix: '+1 pierce', effect: { pierceCountBonus: 1 } },
    { id: 'spear_pushback', name: 'Forceful Thrust', damageBonus: 0.40, suffix: 'push 40px', effect: { knockbackDistance: 40 } },
    { id: 'spear_8', name: 'Ballista Spear', damageBonus: 0.40, effect: {} },
    { id: 'spear_9', name: 'Piercing Tempest', damageBonus: 0.40, suffix: '+1 pierce', effect: { pierceCountBonus: 1 } },
    { id: 'spear_10', name: 'Harpoon of Doom', damageBonus: 0.40, effect: {} },
    { id: 'spear_11', name: 'Lance of Ruin', damageBonus: 0.45, suffix: '+80px knockback', effect: { knockbackDistance: 80 } },
    { id: 'spear_12', name: 'Godspear', damageBonus: 0.45, effect: {} },
    { id: 'spear_13', name: "Gungnir's Edge", damageBonus: 0.45, suffix: '+2 pierce', effect: { pierceCountBonus: 2 } },
    { id: 'spear_14', name: 'World Piercer', damageBonus: 0.45, effect: {} },
    { id: 'spear_15', name: 'Celestial Lance', damageBonus: 0.50, suffix: '+2 pierce', effect: { pierceCountBonus: 2 } },
    { id: 'spear_16', name: 'Stellar Javelin', damageBonus: 0.50, effect: {} },
    { id: 'spear_17', name: 'Galaxy Piercer', damageBonus: 0.85, suffix: '+100px knockback', effect: { knockbackDistance: 100 } },
    { id: 'spear_18', name: 'Nebula Strike', damageBonus: 0.85, suffix: '+2 pierce', effect: { pierceCountBonus: 2 } },
    { id: 'spear_19', name: 'Quasar Spear', damageBonus: 0.90, effect: {} },
    { id: 'spear_20', name: 'Cosmic Harpoon', damageBonus: 0.90, suffix: '+120px knockback', effect: { knockbackDistance: 120 } },
    { id: 'spear_21', name: 'Supernova Lance', damageBonus: 0.95, suffix: '+2 pierce', effect: { pierceCountBonus: 2 } },
    { id: 'spear_22', name: 'Black Hole Javelin', damageBonus: 0.95, effect: {} },
    { id: 'spear_23', name: 'Universe Piercer', damageBonus: 0.95, suffix: '+150px knockback', effect: { knockbackDistance: 150 } },
    { id: 'spear_24', name: 'Void Lance', damageBonus: 1.00, suffix: '+2 pierce', effect: { pierceCountBonus: 2 } },
    { id: 'spear_25', name: 'Omega Spear', damageBonus: 1.00, suffix: '+200px knockback, +3 pierce', effect: { knockbackDistance: 200, pierceCountBonus: 3 } },
  ] as ChainStepDef[]),
  builder: buildRepairChain('builder', [
    { id: 'build_tools', name: 'Better Tools', repairMult: 1.4, effect: {} },
    { id: 'build_reinforce', name: 'Reinforcement', repairMult: 1.4, effect: {} },
    { id: 'build_apprentance', name: 'Reinforcement', repairMult: 1.4, effect: {} },
    { id: 'build_master', name: 'Master Builder', repairMult: 1.4, effect: {} },
    { id: 'build_fortify', name: 'Fortify', noDamage: true, suffix: '+3000 max wall HP', effect: { maxWallHPBonus: 3000 } },
    { id: 'build_emergency', name: 'Emergency Repair', repairMult: 1.4, effect: {} },
    { id: 'build_6', name: 'Legendary Craftsman', repairMult: 1.4, effect: {} },
    { id: 'build_7', name: 'Divine Bricks', repairMult: 1.4, suffix: '+5000 max HP', effect: { maxWallHPBonus: 5000 } },
    { id: 'build_8', name: 'Architect Supreme', repairMult: 1.5, effect: {} },
    { id: 'build_9', name: 'Titan Forge', repairMult: 1.5, effect: {} },
    { id: 'build_10', name: 'Eternal Mason', repairMult: 1.5, effect: {} },
    { id: 'build_11', name: 'Bulwark Master', repairMult: 1.5, suffix: '+7000 max HP', effect: { maxWallHPBonus: 7000 } },
    { id: 'build_12', name: 'Wall of the Gods', repairMult: 1.6, effect: {} },
    { id: 'build_13', name: 'Divine Masonry', repairMult: 1.6, suffix: '+10000 max HP', effect: { maxWallHPBonus: 10000 } },
    { id: 'build_14', name: 'Cosmic Forge', repairMult: 1.6, effect: {} },
    { id: 'build_15', name: 'Stellar Foundation', repairMult: 1.6, effect: {} },
    { id: 'build_16', name: 'Nebula Scaffold', repairMult: 1.7, suffix: '+20000 max HP', effect: { maxWallHPBonus: 20000 } },
    { id: 'build_17', name: 'Galaxy Mason', repairMult: 1.7, effect: {} },
    { id: 'build_18', name: 'Quasar Builder', repairMult: 1.7, suffix: '+50000 max HP', effect: { maxWallHPBonus: 50000 } },
    { id: 'build_19', name: 'Pulsar Fortress', repairMult: 1.8, effect: {} },
    { id: 'build_20', name: 'Supernova Bastion', repairMult: 1.8, effect: {} },
    { id: 'build_21', name: 'Cosmic Citadel', repairMult: 1.8, suffix: '+100000 max HP', effect: { maxWallHPBonus: 100000 } },
    { id: 'build_22', name: 'Universe Rampart', repairMult: 1.8, effect: {} },
    { id: 'build_23', name: 'Omega Fortress', repairMult: 1.9, effect: {} },
    { id: 'build_24', name: 'Eternal Wall', repairMult: 1.9, suffix: '+250000 max HP', effect: { maxWallHPBonus: 250000 } },
    { id: 'build_25', name: 'The Infinite Mason', repairMult: 2.0, effect: {} },
  ] as ChainStepDef[]),
  crow_trainer: buildDamageChain('crow_trainer', [
    { id: 'crow_talons', name: 'Sharp Talons', damageBonus: 0.15, effect: {} },
    { id: 'crow_swift', name: 'Swift Crow', damageBonus: 0.15, suffix: '+15% crow speed', effect: { speedMult: 1.15 } },
    { id: 'crow_hunter', name: 'Trained Hunter', damageBonus: 0.30, effect: {} },
    { id: 'crow_pickpocket', name: 'Pickpocket', damageBonus: 0.30, suffix: '+20g extra per crow', effect: { goldStealBonus: 20 } },
    { id: 'crow_double', name: 'Double Crow', damageBonus: 0.30, suffix: '-20% attack interval', effect: { attackIntervalMult: 0.2 } },
    { id: 'crow_war', name: 'War Crow', damageBonus: 0.40, effect: {} },
    { id: 'crow_larceny', name: 'Grand Larceny', damageBonus: 0.40, suffix: '+30g extra per crow', effect: { goldStealBonus: 30 } },
    { id: 'crow_flock', name: 'Flock', damageBonus: 0.45, suffix: '+1 crow', effect: { extraCrows: 1 } },
    { id: 'crow_murder_size', name: 'Murder Size', damageBonus: 0.50, suffix: '+1 crow', effect: { extraCrows: 1 } },
    { id: 'crow_deep_peck', name: 'Deep Peck', damageBonus: 0.55, suffix: '+50g extra per crow', effect: { goldStealBonus: 50 } },
    { id: 'crow_ravenous', name: 'Ravenous', damageBonus: 0.60, suffix: '-20% attack interval', effect: { attackIntervalMult: 0.8 } },
    { id: 'crow_11', name: 'Raven Swarm', damageBonus: 0.65, effect: {} },
    { id: 'crow_12', name: 'Treasure Hoarders', damageBonus: 0.70, suffix: '+80g extra per crow', effect: { goldStealBonus: 80 } },
    { id: 'crow_13', name: 'Carrion Lords', damageBonus: 0.70, effect: {} },
    { id: 'crow_14', name: 'Death From Above', damageBonus: 0.75, suffix: '+1 crows', effect: { extraCrows: 1 } },
    { id: 'crow_15', name: 'Black Murder', damageBonus: 0.75, suffix: '+2 crows', effect: { extraCrows: 2 } },
    { id: 'crow_16', name: 'Shriek Storm', damageBonus: 0.80, suffix: '+120g extra per crow', effect: { goldStealBonus: 120 } },
    { id: 'crow_17', name: 'Endless Flock', damageBonus: 0.80, suffix: '-20% interval', effect: { attackIntervalMult: 0.8 } },
    { id: 'crow_18', name: 'Shadow Murder', damageBonus: 0.85, suffix: '+3 crows', effect: { extraCrows: 3 } },
    { id: 'crow_19', name: 'Apocalypse Crows', damageBonus: 0.85, suffix: '+200g extra per crow', effect: { goldStealBonus: 200 } },
    { id: 'crow_20', name: 'Death Omen', damageBonus: 0.90, suffix: '+4 crows', effect: { extraCrows: 8 } },
    { id: 'crow_21', name: 'Raven God', damageBonus: 0.90, suffix: '-30% interval', effect: { attackIntervalMult: 0.7 } },
    { id: 'crow_22', name: 'Eternal Darkness', damageBonus: 0.95, suffix: '+400g per crow', effect: { goldStealBonus: 400 } },
    { id: 'crow_23', name: 'Murder of Gods', damageBonus: 0.95, suffix: '+5 crows', effect: { extraCrows: 10 } },
    { id: 'crow_24', name: 'Corvid Armageddon', damageBonus: 1.00, effect: {} },
    { id: 'crow_25', name: 'The Last Crow', damageBonus: 1.00, suffix: '+6 crows, +1000g extra per crow', effect: { extraCrows: 6, goldStealBonus: 1000 } },
  ] as ChainStepDef[]),
  longbow_archer: buildDamageChain('longbow_archer', [
    { id: 'longbow_composite', name: 'Composite Bow', damageBonus: 0.15, suffix: '+10% accuracy', effect: { accuracyPenaltySet: 0.40 } },
    { id: 'longbow_eagle_eye', name: 'Eagle Eye', damageBonus: 0.15, suffix: '+1 pierce', effect: { pierceCountBonus: 1 } },
    { id: 'longbow_heavy_arrows', name: 'Heavy Arrows', damageBonus: 0.22, suffix: '+10% accuracy', effect: { accuracyPenaltySet: 0.30 } },
    { id: 'longbow_fire', name: 'Fire Arrows', noDamage: true, suffix: 'Arrows ignite! 20% dmg/tick, 3s', effect: { fireEnabled: true, firePercentBonus: 0.20, fireDurationBonus: 3000 } },
    { id: 'longbow_mastery', name: 'Mastery', damageBonus: 0.45, effect: {} },
    { id: 'longbow_4', name: 'Recurve Perfection', damageBonus: 0.50, suffix: '+10% accuracy', effect: { accuracyPenaltySet: 0.20 } },
    { id: 'longbow_5', name: 'Sniper Elite', damageBonus: 0.55, effect: {} },
    { id: 'longbow_6', name: 'Steel Arrows', damageBonus: 0.50, effect: {} },
    { id: 'longbow_7', name: 'Hawkeye Legend', damageBonus: 0.55, suffix: '+10% accuracy', effect: { accuracyPenaltySet: 0.10 } },
    { id: 'longbow_inferno', name: 'Inferno Arrows', damageBonus: 0.60, suffix: '+15% fire, +2s burn', effect: { firePercentBonus: 0.15, fireDurationBonus: 2000 } },
    { id: 'longbow_9', name: 'Celestial Arc', damageBonus: 0.65, effect: {} },
    { id: 'longbow_10', name: 'Heaven Pierce', damageBonus: 0.65, suffix: '+5% accuracy', effect: { accuracyPenaltySet: 0.05 } },
    { id: 'longbow_11', name: 'Artemis Bow', damageBonus: 0.65, effect: {} },
    { id: 'longbow_12', name: 'Cosmic Sniper', damageBonus: 0.65, suffix: '+1 pierce', effect: { pierceCountBonus: 1 } },
    { id: 'longbow_13', name: 'Arrow of Infinity', damageBonus: 0.65, effect: {} },
    { id: 'longbow_14', name: 'World Ender Bow', damageBonus: 0.70, effect: {} },
    { id: 'longbow_15', name: 'Stellar Bow', damageBonus: 0.70, suffix: '+20% fire, +3s burn', effect: { firePercentBonus: 0.20, fireDurationBonus: 3000 } },
    { id: 'longbow_16', name: 'Cosmic Archer', damageBonus: 0.70, suffix: '+1 pierce', effect: { pierceCountBonus: 1 } },
    { id: 'longbow_17', name: 'Nebula Bow', damageBonus: 0.85, effect: {} },
    { id: 'longbow_18', name: 'Pulsar Arrow', damageBonus: 0.85, suffix: '+20% fire', effect: { firePercentBonus: 0.40 } },
    { id: 'longbow_19', name: 'Quasar Shot', damageBonus: 0.90, suffix: '5% accuracy (perfect)', effect: { accuracyPenaltySet: 0.05 } },
    { id: 'longbow_20', name: 'Galactic Archer', damageBonus: 0.90, suffix: '+1 pierce', effect: { pierceCountBonus: 1 } },
    { id: 'longbow_21', name: 'Supernova Arrow', damageBonus: 0.95, suffix: '+20% fire, +3s burn', effect: { firePercentBonus: 0.20, fireDurationBonus: 3000 } },
    { id: 'longbow_22', name: 'Universe Bow', damageBonus: 0.95, effect: {} },
    { id: 'longbow_23', name: 'Omega Shot', damageBonus: 0.95, suffix: '+1 pierce, +4s burn', effect: { pierceCountBonus: 1, fireDurationBonus: 4000 } },
    { id: 'longbow_24', name: 'Extinction Arrow', damageBonus: 1.00, effect: {} },
    { id: 'longbow_25', name: 'The Infinite Bow', damageBonus: 1.00, suffix: '+60% fire, +2 pierce', effect: { firePercentBonus: 0.60, pierceCountBonus: 2 } },
  ] as ChainStepDef[]),
  beehive_thrower: buildSwarmChain('beehive_thrower', [
    { id: 'bee_angry', name: 'Angrier Bees', swarmMult: 1.3, effect: {} },
    { id: 'bee_larger', name: 'Larger Swarm', noDamage: true, suffix: '+30% radius', effect: { swarmRadiusMult: 1.3 } },
    { id: 'bee_longer', name: 'Longer Swarm', noDamage: true, suffix: '+3s duration', effect: { swarmDurationBonus: 3000 } },
    { id: 'bee_killer', name: 'Killer Bees', swarmMult: 1.7, effect: {} },
    { id: 'bee_apocalypse', name: 'Bee Apocalypse', noDamage: true, suffix: '+50% radius, +5s', effect: { swarmRadiusMult: 1.5, swarmDurationBonus: 5000 } },
    { id: 'bee_queen', name: 'Queen Bee', noDamage: true, suffix: 'swarm slows 30%', effect: { swarmSlowEffect: 0.3 } },
    { id: 'bee_6', name: 'Hive Mind', swarmMult: 1.8, effect: {} },
    { id: 'bee_7', name: 'Toxic Swarm', swarmMult: 2.0, suffix: '+50% slow', effect: { swarmSlowEffect: 0.5 } },
    { id: 'bee_8', name: 'Murder Hornets', swarmMult: 1.7, effect: {} },
    { id: 'bee_9', name: 'Plague Swarm', swarmMult: 1.8, suffix: '+100% radius', effect: { swarmRadiusMult: 2.0 } },
    { id: 'bee_10', name: 'Insect Armageddon', swarmMult: 2.0, effect: {} },
    { id: 'bee_11', name: 'Bee Apocalypse II', swarmMult: 1.7, suffix: '+8s duration', effect: { swarmDurationBonus: 8000 } },
    { id: 'bee_12', name: 'Hive of Doom', swarmMult: 2.0, effect: {} },
    { id: 'bee_13', name: 'Stinger Storm', swarmMult: 1.7, suffix: '+70% slow', effect: { swarmSlowEffect: 0.7 } },
    { id: 'bee_14', name: 'Extinction Swarm', swarmMult: 1.8, effect: {} },
    { id: 'bee_15', name: 'Stellar Swarm', swarmMult: 2.0, suffix: '+100% radius', effect: { swarmRadiusMult: 2.0 } },
    { id: 'bee_16', name: 'Nebula Hive', swarmMult: 1.8, effect: {} },
    { id: 'bee_17', name: 'Galaxy Sting', swarmMult: 2.0, suffix: '+80% slow', effect: { swarmSlowEffect: 0.8 } },
    { id: 'bee_18', name: 'Quasar Colony', swarmMult: 1.8, suffix: '+10s duration', effect: { swarmDurationBonus: 10000 } },
    { id: 'bee_19', name: 'Pulsar Swarm', swarmMult: 2.0, effect: {} },
    { id: 'bee_20', name: 'Supernova Hive', swarmMult: 1.8, suffix: '+150% radius', effect: { swarmRadiusMult: 2.5 } },
    { id: 'bee_21', name: 'Cosmic Swarm', swarmMult: 2.0, effect: {} },
    { id: 'bee_22', name: 'Universe Sting', swarmMult: 1.8, suffix: '+90% slow', effect: { swarmSlowEffect: 0.9 } },
    { id: 'bee_23', name: 'Extinction Colony', swarmMult: 2.0, suffix: '+15s duration', effect: { swarmDurationBonus: 15000 } },
    { id: 'bee_24', name: 'Omega Swarm', swarmMult: 1.8, effect: {} },
    { id: 'bee_25', name: 'The Infinite Hive', swarmMult: 2.0, suffix: '+200% radius, +95% slow', effect: { swarmRadiusMult: 3.0, swarmSlowEffect: 0.95 } },
  ] as ChainStepDef[]),
  trebuchet: buildDamageChain('trebuchet', [
    { id: 'treb_heavy', name: 'Heavier Load', damageBonus: 0.15, effect: {} },
    { id: 'treb_wider', name: 'Wider Impact', damageBonus: 0.15, suffix: '+20px AoE', effect: { aoeRadiusBonus: 20 } },
    { id: 'treb_siege', name: 'Siege Engine', damageBonus: 0.55, effect: {} },
    { id: 'treb_rain', name: 'Rain of Fire', damageBonus: 0.40, suffix: '+20px AoE', effect: { aoeRadiusBonus: 20 } },
    { id: 'treb_devastation', name: 'Devastation', damageBonus: 0.80, effect: {} },
    { id: 'treb_meteor', name: 'Meteor Strike', damageBonus: 0.75, suffix: '+20px AoE', effect: { aoeRadiusBonus: 20 } },
    { id: 'treb_6', name: 'Doomsday Engine', damageBonus: 0.90, effect: {} },
    { id: 'treb_7', name: 'Cataclysm Launcher', damageBonus: 0.85, suffix: '+20px AoE', effect: { aoeRadiusBonus: 20 } },
    { id: 'treb_8', name: 'World Crusher', damageBonus: 0.55, effect: {} },
    { id: 'treb_9', name: 'Extinction Artillery', damageBonus: 0.60, suffix: '-20% interval', effect: { attackIntervalMult: 0.80 } },
    { id: 'treb_10', name: 'Titan Siege', damageBonus: 0.65, suffix: '+20px AoE', effect: { aoeRadiusBonus: 20 } },
    { id: 'treb_11', name: 'Planet Breaker', damageBonus: 0.70, effect: {} },
    { id: 'treb_12', name: 'Armageddon', damageBonus: 0.70, suffix: '+20px AoE', effect: { aoeRadiusBonus: 20 } },
    { id: 'treb_13', name: 'Cosmic Bombardment', damageBonus: 0.75, suffix: '-20% interval', effect: { attackIntervalMult: 0.80 } },
    { id: 'treb_14', name: 'Universe Ender', damageBonus: 0.75, effect: {} },
    { id: 'treb_15', name: 'Stellar Impact', damageBonus: 0.80, suffix: '+20px AoE', effect: { aoeRadiusBonus: 20 } },
    { id: 'treb_16', name: 'Nebula Strike', damageBonus: 0.80, effect: {} },
    { id: 'treb_17', name: 'Galaxy Crusher', damageBonus: 0.85, suffix: '-20% interval', effect: { attackIntervalMult: 0.80 } },
    { id: 'treb_18', name: 'Quasar Barrage', damageBonus: 0.85, suffix: '+20px AoE', effect: { aoeRadiusBonus: 20 } },
    { id: 'treb_19', name: 'Supernova Launch', damageBonus: 0.90, effect: {} },
    { id: 'treb_20', name: 'Black Hole Shot', damageBonus: 0.90, suffix: '+20px AoE', effect: { aoeRadiusBonus: 20 } },
    { id: 'treb_21', name: 'Cosmic Extinction', damageBonus: 0.95, suffix: '-20% interval', effect: { attackIntervalMult: 0.80 } },
    { id: 'treb_22', name: 'Universe Shatter', damageBonus: 0.95, effect: {} },
    { id: 'treb_23', name: 'Dimensional Rift', damageBonus: 0.95, suffix: '+20px AoE', effect: { aoeRadiusBonus: 20 } },
    { id: 'treb_24', name: 'Reality Collapse', damageBonus: 1.00, suffix: '-25% interval', effect: { attackIntervalMult: 0.75 } },
    { id: 'treb_25', name: 'The Absolute Trebuchet', damageBonus: 1.00, suffix: '+20px AoE', effect: { aoeRadiusBonus: 20 } },
  ] as ChainStepDef[]),
  crossbow: buildDamageChain('crossbow', [
    { id: 'xbow_heavy', name: 'Heavy Bolts', damageBonus: 0.20, effect: {} },
    { id: 'xbow_scope', name: 'Scope Sight', damageBonus: 0.15, effect: {} },
    { id: 'xbow_pierce', name: 'Armor Piercing', noDamage: true, suffix: '+1 pierce', effect: { pierceCountBonus: 1 } },
    { id: 'xbow_rapid', name: 'Rapid Reload', noDamage: true, suffix: '-15% attack interval', effect: { attackIntervalMult: 0.85 } },
    { id: 'xbow_mastery', name: 'Crossbow Mastery', damageBonus: 0.55, effect: {} },
    { id: 'xbow_snipe', name: 'Sniper Bolts', damageBonus: 0.75, suffix: '+1 pierce', effect: { pierceCountBonus: 1 } },
    { id: 'xbow_6', name: 'Steel-Tipped', damageBonus: 0.85, effect: {} },
    { id: 'xbow_7', name: 'Repeating Crossbow', damageBonus: 0.90, suffix: '-20% interval', effect: { attackIntervalMult: 0.80 } },
    { id: 'xbow_8', name: 'Dragon Bolts', damageBonus: 0.55, effect: {} },
    { id: 'xbow_9', name: 'Siege Crossbow', damageBonus: 0.60, suffix: '+2 pierce', effect: { pierceCountBonus: 2 } },
    { id: 'xbow_10', name: 'Titan Bolts', damageBonus: 0.65, effect: {} },
    { id: 'xbow_11', name: 'Lightning Bolts', damageBonus: 0.70, effect: {} },
    { id: 'xbow_12', name: 'Doom Crossbow', damageBonus: 0.70, suffix: '+3 pierce', effect: { pierceCountBonus: 3 } },
    { id: 'xbow_13', name: 'World Piercer', damageBonus: 0.75, effect: {} },
    { id: 'xbow_14', name: 'Infinity Crossbow', damageBonus: 0.75, effect: {} },
    { id: 'xbow_15', name: 'Stellar Bolt', damageBonus: 0.80, suffix: '+3 pierce', effect: { pierceCountBonus: 3 } },
    { id: 'xbow_16', name: 'Cosmic Crossbow', damageBonus: 0.80, effect: {} },
    { id: 'xbow_17', name: 'Nebula Bolt', damageBonus: 0.85, suffix: '-20% interval', effect: { attackIntervalMult: 0.80 } },
    { id: 'xbow_18', name: 'Galaxy Crossbow', damageBonus: 0.85, effect: {} },
    { id: 'xbow_19', name: 'Quasar Bolt', damageBonus: 0.90, suffix: '+4 pierce', effect: { pierceCountBonus: 4 } },
    { id: 'xbow_20', name: 'Pulsar Crossbow', damageBonus: 0.90, effect: {} },
    { id: 'xbow_21', name: 'Supernova Bolt', damageBonus: 0.95, suffix: '-25% interval', effect: { attackIntervalMult: 0.75 } },
    { id: 'xbow_22', name: 'Void Crossbow', damageBonus: 0.95, suffix: '+5 pierce', effect: { pierceCountBonus: 5 } },
    { id: 'xbow_23', name: 'Universe Bolt', damageBonus: 0.95, effect: {} },
    { id: 'xbow_24', name: 'Omega Crossbow', damageBonus: 1.00, suffix: '-30% interval', effect: { attackIntervalMult: 0.70 } },
    { id: 'xbow_25', name: 'The Infinite Bolt', damageBonus: 1.00, suffix: '+6 pierce', effect: { pierceCountBonus: 6 } },
  ] as ChainStepDef[]),
  firefly_nest: buildDamageChain('firefly_nest', [
    { id: 'fly_swarm', name: 'Larger Swarm', noDamage: true, suffix: '+2 fireflies', effect: { extraProjectiles: 2 } },
    { id: 'fly_burn', name: 'Hotter Flames', damageBonus: 0.30, effect: {} },
    { id: 'fly_spread', name: 'Wider Blast', noDamage: true, suffix: '+25% AoE', effect: { aoeRadiusBonus: 12 } },
    { id: 'fly_ignite', name: 'Ignition', noDamage: true, suffix: '+25% fire damage, +2s burn', effect: { firePercentBonus: 0.25, fireDurationBonus: 2000 } },
    { id: 'fly_inferno', name: 'Inferno Swarm', damageBonus: 0.80, effect: {} },
    { id: 'fly_napalm', name: 'Napalm Fireflies', damageBonus: 0.90, suffix: '+50% AoE', effect: { aoeRadiusBonus: 25 } },
    { id: 'fly_6', name: 'Phoenix Swarm', damageBonus: 0.85, effect: {} },
    { id: 'fly_7', name: 'Firestorm', damageBonus: 0.90, suffix: '+4 fireflies', effect: { extraProjectiles: 4 } },
    { id: 'fly_8', name: 'Solar Flare', damageBonus: 0.55, effect: {} },
    { id: 'fly_9', name: 'Hellfire Swarm', damageBonus: 0.60, suffix: '+50% fire', effect: { firePercentBonus: 0.50 } },
    { id: 'fly_10', name: 'Supernova Bugs', damageBonus: 0.65, effect: {} },
    { id: 'fly_11', name: 'Apocalypse Swarm', damageBonus: 0.70, suffix: '+100% AoE', effect: { aoeRadiusBonus: 50 } },
    { id: 'fly_12', name: 'Extinction Fire', damageBonus: 0.70, effect: {} },
    { id: 'fly_13', name: 'Star Swarm', damageBonus: 0.75, effect: {} },
    { id: 'fly_14', name: 'Big Bang Bugs', damageBonus: 0.75, effect: {} },
    { id: 'fly_15', name: 'Stellar Swarm', damageBonus: 0.80, suffix: '+6 fireflies', effect: { extraProjectiles: 6 } },
    { id: 'fly_16', name: 'Nebula Blaze', damageBonus: 0.80, suffix: '+75% fire', effect: { firePercentBonus: 0.75 } },
    { id: 'fly_17', name: 'Galaxy Fire', damageBonus: 0.85, effect: {} },
    { id: 'fly_18', name: 'Quasar Swarm', damageBonus: 0.85, suffix: '+100% AoE', effect: { aoeRadiusBonus: 50 } },
    { id: 'fly_19', name: 'Pulsar Blaze', damageBonus: 0.90, suffix: '+8 fireflies', effect: { extraProjectiles: 8 } },
    { id: 'fly_20', name: 'Supernova Swarm', damageBonus: 0.90, suffix: '+100% fire', effect: { firePercentBonus: 1.00 } },
    { id: 'fly_21', name: 'Cosmic Hive', damageBonus: 0.95, suffix: '+125% AoE', effect: { aoeRadiusBonus: 60 } },
    { id: 'fly_22', name: 'Universe Flame', damageBonus: 0.95, suffix: '+10 fireflies', effect: { extraProjectiles: 10 } },
    { id: 'fly_23', name: 'Extinction Blaze', damageBonus: 0.95, effect: {} },
    { id: 'fly_24', name: 'Omega Swarm', damageBonus: 1.00, suffix: '+150% AoE, +125% fire', effect: { aoeRadiusBonus: 75, firePercentBonus: 1.25 } },
    { id: 'fly_25', name: 'The Eternal Flame', damageBonus: 1.00, suffix: '+15 fireflies', effect: { extraProjectiles: 15 } },
  ] as ChainStepDef[]),
  druid: buildDamageChain('druid', [
    { id: 'druid_thorns', name: 'Thorny Vines', damageBonus: 0.30, effect: {} },
    { id: 'druid_roots', name: 'Deep Roots', damageBonus: 0.50, effect: {} },
    { id: 'druid_entangle', name: 'Entangle', noDamage: true, suffix: '+20% slow', effect: { slowEffectBonus: 0.20 } },
    { id: 'druid_growth', name: 'Rapid Growth', noDamage: true, suffix: '-20% cast interval, +25% size', effect: { attackIntervalMult: 0.80, brambleSizeMult: 0.25 } },
    { id: 'druid_poison', name: 'Poison Thorns', damageBonus: 0.80, effect: {} },
    { id: 'druid_ancient', name: 'Ancient Grove', damageBonus: 0.90, suffix: '+25% size', effect: { brambleSizeMult: 0.25 } },
    { id: 'druid_6', name: 'Forest Wrath', damageBonus: 0.85, effect: {} },
    { id: 'druid_7', name: 'Living Wall', damageBonus: 0.90, suffix: '+30% slow', effect: { slowEffectBonus: 0.30 } },
    { id: 'druid_8', name: 'Primal Brambles', damageBonus: 0.55, suffix: '+50% size', effect: { brambleSizeMult: 0.50 } },
    { id: 'druid_9', name: 'World Tree Roots', damageBonus: 0.60, effect: {} },
    { id: 'druid_10', name: "Nature's Fury", damageBonus: 0.65, effect: {} },
    { id: 'druid_11', name: 'Overgrowth', damageBonus: 0.70, suffix: '-25% interval, +75% size', effect: { attackIntervalMult: 0.75, brambleSizeMult: 0.75 } },
    { id: 'druid_12', name: 'Jungle Apocalypse', damageBonus: 0.70, effect: {} },
    { id: 'druid_13', name: "Gaia's Wrath", damageBonus: 0.75, effect: {} },
    { id: 'druid_14', name: 'Eternal Forest', damageBonus: 0.75, effect: {} },
    { id: 'druid_15', name: 'Thorned Titan', damageBonus: 0.80, suffix: '+25% slow', effect: { slowEffectBonus: 0.25 } },
    { id: 'druid_16', name: 'Root Network', damageBonus: 0.80, suffix: '+100% size', effect: { brambleSizeMult: 1.00 } },
    { id: 'druid_17', name: 'Jungle Titan', damageBonus: 0.85, suffix: '-20% interval', effect: { attackIntervalMult: 0.80 } },
    { id: 'druid_18', name: 'Primordial Roots', damageBonus: 0.85, effect: {} },
    { id: 'druid_19', name: 'Verdant Fury', damageBonus: 0.90, suffix: '+30% slow', effect: { slowEffectBonus: 0.30 } },
    { id: 'druid_20', name: 'Forest Immortal', damageBonus: 0.90, suffix: '+150% size', effect: { brambleSizeMult: 1.50 } },
    { id: 'druid_21', name: "Gaia's Avatar", damageBonus: 0.95, suffix: '-25% interval', effect: { attackIntervalMult: 0.75 } },
    { id: 'druid_22', name: 'World Bramble', damageBonus: 0.95, effect: {} },
    { id: 'druid_23', name: "Nature's Apocalypse", damageBonus: 0.95, suffix: '+200% size', effect: { brambleSizeMult: 2.00 } },
    { id: 'druid_24', name: 'Eternal Growth', damageBonus: 1.00, effect: {} },
    { id: 'druid_25', name: 'Verdant Extinction', damageBonus: 1.00, suffix: '-30% interval, +200% slow cap', effect: { attackIntervalMult: 0.70 } },
  ] as ChainStepDef[]),
  lightning_wizard: buildDamageChain('lightning_wizard', [
    { id: 'wiz_focus', name: 'Focused Energy', damageBonus: 0.25, effect: {} },
    { id: 'wiz_chain', name: 'Chain Mastery', noDamage: true, suffix: '+1 chain', effect: { extraProjectiles: 1 } },
    { id: 'wiz_surge', name: 'Power Surge', damageBonus: 0.50, effect: {} },
    { id: 'wiz_rapid', name: 'Rapid Casting', noDamage: true, suffix: '-15% cast interval', effect: { attackIntervalMult: 0.85 } },
    { id: 'wiz_storm', name: 'Storm Call', damageBonus: 0.75, suffix: '+1 chain', effect: { extraProjectiles: 1 } },
    { id: 'wiz_tempest', name: 'Tempest', damageBonus: 0.85, effect: {} },
    { id: 'wiz_6', name: 'Thunder God', damageBonus: 0.90, effect: {} },
    { id: 'wiz_7', name: 'Arc Lightning', damageBonus: 0.55, suffix: '+2 chains', effect: { extraProjectiles: 2 } },
    { id: 'wiz_8', name: 'Plasma Storm', damageBonus: 0.60, effect: {} },
    { id: 'wiz_9', name: 'Electron Cascade', damageBonus: 0.65, effect: {} },
    { id: 'wiz_10', name: 'Tesla Coil', damageBonus: 0.70, suffix: '+3 chains', effect: { extraProjectiles: 3 } },
    { id: 'wiz_11', name: 'Ball Lightning', damageBonus: 0.70, effect: {} },
    { id: 'wiz_12', name: 'Mjolnir', damageBonus: 0.75, effect: {} },
    { id: 'wiz_13', name: "Zeus' Wrath", damageBonus: 0.75, suffix: '+5 chains', effect: { extraProjectiles: 5 } },
    { id: 'wiz_14', name: 'Cosmic Storm', damageBonus: 0.80, effect: {} },
    { id: 'wiz_15', name: 'Singularity', damageBonus: 0.80, suffix: '+6 chains', effect: { extraProjectiles: 6 } },
    { id: 'wiz_16', name: 'Dark Matter', damageBonus: 0.85, suffix: '-20% interval', effect: { attackIntervalMult: 0.80 } },
    { id: 'wiz_17', name: 'Neutron Blast', damageBonus: 0.85, effect: {} },
    { id: 'wiz_18', name: 'Pulsar Strike', damageBonus: 0.90, suffix: '+7 chains', effect: { extraProjectiles: 7 } },
    { id: 'wiz_19', name: 'Magnetar Fury', damageBonus: 0.90, suffix: '-20% interval', effect: { attackIntervalMult: 0.80 } },
    { id: 'wiz_20', name: 'Quasar Cascade', damageBonus: 0.95, effect: {} },
    { id: 'wiz_21', name: 'Hawking Radiation', damageBonus: 0.95, suffix: '+8 chains', effect: { extraProjectiles: 8 } },
    { id: 'wiz_22', name: 'Galactic Storm', damageBonus: 0.95, suffix: '-25% interval', effect: { attackIntervalMult: 0.75 } },
    { id: 'wiz_23', name: 'Big Crunch', damageBonus: 1.00, suffix: '+9 chains', effect: { extraProjectiles: 9 } },
    { id: 'wiz_24', name: 'Heat Death', damageBonus: 1.00, effect: {} },
    { id: 'wiz_25', name: 'Omega Lightning', damageBonus: 1.00, suffix: '+10 chains, -25% interval', effect: { extraProjectiles: 10, attackIntervalMult: 0.75 } },
  ] as ChainStepDef[]),
  boomerang_thrower: buildDamageChain('boomerang_thrower', [
    { id: 'boom_1', name: 'Sharpened Edge', damageBonus: 0.25, effect: {} },
    { id: 'boom_2', name: 'Wide Arc', noDamage: true, suffix: '+1 pierce per pass', effect: { pierceCountBonus: 1 } },
    { id: 'boom_3', name: 'Quickspin', damageBonus: 0.40, suffix: '-15% throw interval', effect: { attackIntervalMult: 0.85 } },
    { id: 'boom_4', name: 'Heavy Boomerang', damageBonus: 0.60, effect: {} },
    { id: 'boom_5', name: 'Returning Fury', damageBonus: 0.70, suffix: '+1 pierce per pass', effect: { pierceCountBonus: 1 } },
    { id: 'boom_6', name: 'Whirlwind', damageBonus: 0.80, effect: {} },
    { id: 'boom_7', name: 'Double Throw', noDamage: true, suffix: '+1 boomerang', effect: { extraProjectiles: 1 } },
    { id: 'boom_8', name: 'Razor Wing', damageBonus: 0.90, suffix: '+2 pierce per pass', effect: { pierceCountBonus: 2 } },
    { id: 'boom_9', name: 'Swift Return', damageBonus: 0.85, suffix: '-20% throw interval', effect: { attackIntervalMult: 0.80 } },
    { id: 'boom_10', name: 'Storm Wing', damageBonus: 0.90, effect: {} },
    { id: 'boom_11', name: 'Cyclone', damageBonus: 0.90, suffix: '+1 boomerang', effect: { extraProjectiles: 1 } },
    { id: 'boom_12', name: 'Ancestral Throw', damageBonus: 0.95, effect: {} },
    { id: 'boom_13', name: 'Bloodwing', damageBonus: 0.95, suffix: '+3 pierce per pass', effect: { pierceCountBonus: 3 } },
    { id: 'boom_14', name: 'Infinite Loop', damageBonus: 1.00, suffix: '-25% throw interval', effect: { attackIntervalMult: 0.75 } },
    { id: 'boom_15', name: 'Eternal Vortex', damageBonus: 1.00, suffix: '+2 boomerangs', effect: { extraProjectiles: 2 } },
  ] as ChainStepDef[]),
  wall_crossbow_top: [
    { id: 'wct_1', name: 'Iron Bolts', description: '+50% damage', cost: 350000000, effect: { damageMultBonus: 0.50 } },
    { id: 'wct_2', name: 'Tensioned String', description: '-25% attack interval', cost: 800000000, effect: { attackIntervalMult: 0.75 } },
    { id: 'wct_3', name: 'Barbed Tips', description: '+1 pierce', cost: 1800000000, effect: { pierceCountBonus: 1 } },
    { id: 'wct_4', name: 'Eagle Eye', description: '+200 range', cost: 4000000000, effect: { rangeBonus: 200 } },
    { id: 'wct_5', name: 'Repeating Mechanism', description: '-40% attack interval', cost: 9000000000, effect: { attackIntervalMult: 0.60 } },
  ],
  wall_crossbow_bottom: [
    { id: 'wcb_1', name: 'Reinforced Frame', description: '+50% damage', cost: 1000000000, effect: { damageMultBonus: 0.50 } },
    { id: 'wcb_2', name: 'Quick Load', description: '-25% attack interval', cost: 2500000000, effect: { attackIntervalMult: 0.75 } },
    { id: 'wcb_3', name: 'Broadhead Bolts', description: '+1 pierce, +30px AoE', cost: 6000000000, effect: { pierceCountBonus: 1, aoeRadiusBonus: 30 } },
    { id: 'wcb_4', name: 'Fortified Mount', description: '+200 range, +50% damage', cost: 14000000000, effect: { rangeBonus: 200, damageMultBonus: 0.50 } },
    { id: 'wcb_5', name: 'Siege Ballista', description: '-40% attack interval, +2 pierce', cost: 35000000000, effect: { attackIntervalMult: 0.60, pierceCountBonus: 2 } },
  ],
};

// Legacy alias for BASE_COSTS — points to BUY_BASE_COSTS (stale values removed)
export const BASE_COSTS: Record<string, number> = BUY_BASE_COSTS;

// ---- GLOBAL UPGRADES ----
// click_damage, builder_efficiency, Gold Bonus, Range Boost, All Damage removed
// — damage/gold now handled by chain upgrades and Prestige innate bonuses

// ---- PRICE SCALING ----
export const PRICE_SCALE_FACTOR = 1.5;
export function getUpgradeCost(baseCost: number, level: number): number {
  return Math.round(baseCost * Math.pow(PRICE_SCALE_FACTOR, level));
}

// ---- UNIT STATS ----
// All base stats come from src/balance.ts UNIT_STATS — edit there to tune.
export const ROCK_THROWER = { ...UNIT_STATS.rock_thrower, extraProjectilesPerLevel: 1 };
export const ARCHER = { ...UNIT_STATS.archer };
export const SPEAR_THROWER = { ...UNIT_STATS.spear_thrower, piercing: true };
export const BUILDER = { ...UNIT_STATS.builder };
export const CROW_TRAINER = { ...UNIT_STATS.crow_trainer };
export const TREBUCHET = { ...UNIT_STATS.trebuchet };
export const BEEHIVE_THROWER = { ...UNIT_STATS.beehive_thrower };
export const CROSSBOW = { ...UNIT_STATS.crossbow };
export const FIREFLY_NEST = { ...UNIT_STATS.firefly_nest };
export const DRUID = {
  ...UNIT_STATS.druid,
  brambleHP: 120,  // Legacy min (unused when baseDamage > 0)
  brambleDps: 10,  // Legacy (unused when baseDamage > 0)
};
export const LIGHTNING_WIZARD = {
  ...UNIT_STATS.lightning_wizard,
  orbX: 550,  // Mid-battlefield sky position
  orbY: 60,
};
export const CLUB_SWINGER = { ...UNIT_STATS.club_swinger };
export const SLING_THROWER = { ...UNIT_STATS.sling_thrower, baseSpeed: 0 };
export const LONGBOW_ARCHER = { ...UNIT_STATS.longbow_archer, baseSpeed: 0 };

// ---- WALL UPGRADE ----
export const WALL_UPGRADE_HP_PER_LEVEL = 0; // Legacy — not used (kept for ShopPanel import)
export const WALL_HP_BASE = WALL.upgradeBaseHP;

// ---- THE FINGER (click damage) ----
export const FINGER = {
  ...UNIT_STATS.finger,
  chainBase: 20,           // Chain upgrade cost base
};

// ---- ENEMIES — moved to balance.ts ----
export const ENEMY_BASE = _ENEMY_BASE;

// ---- ENEMY TYPE MULTIPLIERS — moved to balance.ts ----
export const ENEMY_TYPES = _ENEMY_TYPES;

// ---- BOSS DEFINITIONS — moved to balance.ts ----
export const BOSS_DEFINITIONS = _BOSS_DEFINITIONS;

// ---- WAVES — moved to balance.ts ----
export const WAVE = _WAVE;

// ---- GUARANTEED SPAWNS ----
// Each entry: once wave >= introWave, that many of that enemy type are always
// included in every wave's spawn pool (on top of the normal random pool).
export const GUARANTEED_SPAWNS: { introWave: number; type: EnemyType; count: number }[] = [
  { introWave: 3, type: 'runner', count: 2 },
  { introWave: 5, type: 'brute', count: 2 },
  { introWave: 5, type: 'archer', count: 2 },
  { introWave: 8, type: 'shaman', count: 2 },
  { introWave: 10, type: 'shieldbearer', count: 2 },
  { introWave: 20, type: 'longbow_archer', count: 3 },
  { introWave: 20, type: 'saboteur', count: 2 },
  { introWave: 25, type: 'swarmling', count: 4 },
  { introWave: 30, type: 'war_drummer', count: 2 },
  { introWave: 35, type: 'splitter', count: 3 },
  { introWave: 45, type: 'nest_carrier', count: 2 },
  { introWave: 50, type: 'siege_tower', count: 2 },
  { introWave: 55, type: 'enemy_trebuchet', count: 2 },
];

// ---- BUILDING POSITIONS (turrets) ----
export const BUILDING_POSITIONS: Record<string, { x: number; y: number }> = {
  below_1: { x: WALL_X - 40, y: WALL_Y_END - 10 },
  below_2: { x: WALL_X - 40, y: WALL_Y_START + 30 },
  behind_1: { x: 40, y: 170 },
  behind_2: { x: 40, y: 400 },
};

// ---- COLORS ----
export const COLORS = {
  sky: 0x87ceeb,
  skyTop: 0x68b8de,
  skyHorizon: 0xb8dcea,
  grassLight: 0x5cb85c,
  grassDark: 0x4caf50,
  grassFar: 0x7acc7a,
  grassNear: 0x4da84d,
  dirtPath: 0xc4a96a,
  dirtPathDark: 0xb0965c,
  baseBg: 0x8d8d8d,
  baseFloor: 0x999999,
  baseFloorLight: 0xa8a8a8,
  wall: 0x7a7a7a,
  wallDamaged: 0xcc4444,
  wallTop: 0x9e9e9e,
  fence: 0x666666,
  fencePost: 0x555555,
  enemy: 0x333333,
  enemyHit: 0xff4444,
  rock: 0x777777,
  spear: 0x8b4513,
  gold: 0xffd700,
  uiBackground: 0x2d2d2d,
  uiBackgroundLight: 0x3d3d3d,
  uiText: 0xffffff,
  uiTextDim: 0x888888,
  hpBarGreen: 0x44cc44,
  hpBarYellow: 0xcccc44,
  hpBarRed: 0xcc4444,
  hpBarBg: 0x333333,
  buttonNormal: 0x2d4a7a,
  buttonHover: 0x3d6a9a,
  buttonDisabled: 0x444444,
  startWaveButton: 0xd4a017,
  startWaveHover: 0xe8b830,
  slotEmpty: 0xbbbbbb,
  slotOccupied: 0x77cc77,
  tabActive: 0x3a6ea5,
  tabInactive: 0x2a2a3a,
  tabAccent: 0x4a9eff,
  upgradeButton: 0x2a6a3a,
  upgradeButtonHover: 0x3a8a4a,
};

// ---- SKILLS (exponential scaling: damage = base × growth^level) ----
// Buy costs spread across early/mid/late game; cooldowns start long, reduce with upgrades
export const SKILLS = {
  poison_arrows: {
    baseDamage: 8,             // poison DPS at level 1
    damageGrowth: 1.5,        // ×1.5 per level
    baseDuration: 3000,
    durationPerLevel: 500,
    baseCooldown: 45000,       // 45s base — powerful AoE needs long CD
    cooldownReduction: 2500,   // min ~7.5s at high levels
    buyCost: 1500,
    name: 'Poison Arrows',
    description: 'Rain poison arrows on all enemies',
  },
  rock_rain: {
    baseDamage: 40,
    damageGrowth: 1.6,        // ×1.6 per level — targeted so scales harder
    radius: 100,
    baseCooldown: 35000,       // 35s base — targeted burst
    cooldownReduction: 2000,   // min ~7s at high levels
    buyCost: 8000,
    name: 'Rock Rain',
    description: 'Rain rocks on enemies in an area',
  },
  gold_rush: {
    baseMultiplier: 2.0,
    multiplierPerLevel: 0.3,  // +0.3x per level (slower ramp)
    baseDuration: 6000,
    durationPerLevel: 750,
    baseCooldown: 60000,       // 60s base — economic advantage needs patience
    cooldownReduction: 3000,   // min ~12s at high levels
    buyCost: 25000,
    name: 'Gold Rush',
    description: 'Multiply gold from kills',
  },
  lightning_strike: {
    baseDamage: 250,
    damageGrowth: 1.6,        // ×1.6 per level — single target nuke
    baseChainCount: 0,
    chainsPerLevel: 1,
    chainDamageDecay: 0.6,
    baseCooldown: 50000,       // 50s base — devastating nuke
    cooldownReduction: 3000,   // min ~8s at high levels
    buyCost: 75000,
    name: 'Lightning',
    description: 'Massive damage to strongest enemy',
  },
};

export type SkillId = keyof typeof SKILLS;

// ---- ENEMY SPAWN ----
export const ENEMY_SPAWN_X = GAME_WIDTH + 30;
export const ENEMY_MIN_Y = HORIZON_Y + 40;
export const ENEMY_MAX_Y = 490;

// ---- PRESTIGE SYSTEM ----
// legacyDivisor comes from src/balance.ts PRESTIGE_BALANCE — edit there.
export const PRESTIGE = {
  legacyDivisor: PRESTIGE_BALANCE.legacyDivisor,
};

// ---- ELDER COUNCIL — moved to balance.ts ----
export const ELDER_COUNCIL = _ELDER_COUNCIL;

// ---- WALL CROSSBOW TURRETS ----
// Base stats come from src/balance.ts UNIT_STATS — edit there to tune.
export const WALL_CROSSBOW_TOP = {
  ...UNIT_STATS.wall_crossbow_top,
  buyCost: UNIT_BUY_COSTS.wall_crossbow_top,
  y: WALL_Y_START + 10,
};

export const WALL_CROSSBOW_BOTTOM = {
  ...UNIT_STATS.wall_crossbow_bottom,
  buyCost: UNIT_BUY_COSTS.wall_crossbow_bottom,
  y: WALL_Y_END - 10,
};

export const BOOMERANG_THROWER = {
  ...UNIT_STATS.boomerang_thrower,
  buyCost: UNIT_BUY_COSTS.boomerang_thrower,
};

// ---- OFFLINE GAIN ----
export const OFFLINE_GAIN = {
  baseMaxHours: 3,       // Default cap before prestige upgrades
  efficiency: 0.20,      // Fraction of theoretical online GPS awarded offline
  minMinutes: 1,         // Minimum away time before popup appears
};

